<template>
    <div class="apply-remove-tag-process-modal">
        <ProcessModal
            data-qa="apply-remove-tag-modal"
            :is-open="isOpen"
            :summary-text="$t('funnel.apply.remove.tag.summaryText')"
            :title="$t('funnel.apply.remove.tag.title')"
            :show-save="showSave"
            @close-modal="closeModal"
            @update-config="updateConfig"
        >
            <template #processParameters>
                <DsRadioGroup
                    v-model="selectedOption"
                    data-qa="apply-remove-tag-option"
                    :options="options"
                    class="apply-tag-radio-group"
                />
                <DsMultiselect
                    v-model="selectedTags"
                    data-qa="apply-remove-tag-tags"
                    :force-invalid="validationErrorMessages !== null"
                    multiple
                    searchable
                    required
                    :placeholder="$t('funnel.apply.remove.tag.tagLabel')"
                    :disabled="isNewTagVisible"
                    :options="getCategorizedTags"
                    :add-text="tagAddTextLabel"
                    allow-add
                    @updateSearch="updateSearch"
                    @add="openAddNewTag"
                >
                    <template #help>
                        <span v-if="validationErrorMessages !== null">
                            {{ validationErrorMessages }}
                        </span>
                    </template>
                    <template #selection="{ option, remove }">
                        <DsChip
                            remove
                            with-interaction
                            @mousedown.native.stop.prevent="remove"
                        >
                            <template #leading>
                                <DsIcon :name="option.icon" />
                            </template>
                            <template #default>
                                {{ `${option.categoryName} ${option.label}` }}
                            </template>
                        </DsChip>
                    </template>
                </DsMultiselect>
                <DsInputField
                    v-if="isNewTagVisible"
                    v-model.trim="newTagName"
                    :force-invalid="newTagNameError !== null"
                    required
                    data-qa="new-tag-name"
                    class="input-field"
                    type="text"
                    :label="$t('funnel.apply.remove.tag.addNewTagError')"
                >
                    <template #help>
                        <span v-if="newTagNameError !== null">
                            {{ newTagNameError }}
                        </span>
                    </template>
                </DsInputField>
                <DsMultiselect
                    v-if="isNewTagVisible"
                    v-model="selectedCategoryNew"
                    data-qa="new-tag-applied-categories-add"
                    label-prop="label"
                    value-prop="id"
                    :force-invalid="apiErrorMessages !== null"
                    searchable
                    :options="categories"
                    :placeholder="newCategoryLabel"
                >
                    <template #help>
                        <span v-if="apiErrorMessages !== null">
                            {{ apiErrorMessages }}
                        </span>
                    </template>
                </DsMultiselect>
                <DsInputField
                    v-if="isNewTagVisible && isNewCategoryVisible"
                    v-model.trim="newCategoryName"
                    :force-invalid="newTagError !== null"
                    required
                    data-qa="new-category-name"
                    class="input-field"
                    type="text"
                    :label="$t('funnel.apply.remove.tag.createNewCategory')"
                >
                    <template #help>
                        <span v-if="newTagError !== null">
                            {{ newTagError }}
                        </span>
                    </template>
                </DsInputField>
                <DsFilledButton v-if="isNewTagVisible" data-qa="save-button-new-tag" @click="addNewTag">
                    {{ $t('funnel.apply.remove.tag.save') }}
                </DsFilledButton>
                &nbsp;
                <DsLink v-if="isNewTagVisible" data-qa="cancel-button-tag" @click="endNewTag">
                    {{ $t('funnel.apply.remove.tag.cancel') }}
                </DsLink>
            </template>
        </ProcessModal>
    </div>
</template>

<script>
import ProcessModal from '../processModal/ProcessModal.vue';

import {
    createNewCategory,
    createNewTag,
    loadCategories,
    loadTags,
    loadCategorizedTags,
} from '../../../whens/tagApplied/tag.applied.api';

export const APPLY_REMOVE_TYPE_IDS = {
    APPLY: 'true',
    REMOVE: 'false',
};

export default {

    components: {
        ProcessModal,
    },

    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    tagIds: [],
                    isApply: true,
                },
            }),
        },
    },

    emits: ['update-cell-value', 'close-modal'],

    data() {
        return {
            tagInput: '',
            categories: [],
            categorizedTags: [],
            isNewTagOpen: false,
            newTagError: null,
            newTagNameError: null,
            newTagExistError: null,
            newTagName: '',
            showSave: true,
            newCategoryName: '',
            newTagLabel: this.$t('funnel.apply.remove.tag.tagName'),
            newCategoryLabel: this.$t('funnel.apply.remove.tag.newCategoryLabel'),
            selectedCategoryNew: null,
            selectedTags: [],
            tags: [],
            selectedOption: this.cell?.value?.isApply ?? APPLY_REMOVE_TYPE_IDS.APPLY,
            validationErrorMessages: null,
            apiErrorMessages: null,
            options: [
                { value: APPLY_REMOVE_TYPE_IDS.APPLY, label: this.$t('funnel.apply.remove.tag.applyLabel') },
                { value: APPLY_REMOVE_TYPE_IDS.REMOVE, label: this.$t('funnel.apply.remove.tag.removeLabel') },
            ],
        };
    },

    computed: {
        tagAddTextLabel() {
            if (this.tagInput?.trim()?.length > 0) {
                return `${this.$t('funnel.apply.remove.tag.add')} "${this.tagInput}" ${this.$t('funnel.apply.remove.tag.asNew')}`;
            }

            return this.$t('funnel.apply.remove.tag.addNew');
        },
        getTags() {
            return this.tags?.filter((option) => !this.selectedTags?.find((o) => o.label === option.label));
        },
        getCategorizedTags() {
            const filteredCategorizedTags = [];

            Object.keys(this.categorizedTags).forEach((key) => {
                let elementOptions = this.categorizedTags[key]?.options;

                Object.keys(elementOptions).forEach((keyOpt) => {
                    const elementsFound = this.selectedTags?.filter((o) => o === elementOptions[keyOpt]?.value);

                    if (elementsFound) {
                        elementOptions = elementOptions?.filter((option) => !elementsFound?.find((o) => o === option.value));
                    }
                });
                filteredCategorizedTags.push({ label: this.categorizedTags[key].label, options: elementOptions });
            });

            return filteredCategorizedTags;
        },
        isNewCategoryVisible() {
            return this.selectedCategoryNew?.id === -1;
        },
        isNewTagVisible() {
            return this.isNewTagOpen;
        },
    },

    async mounted() {
        await this.loadCategorizedTagsDropdown().finally(async () => {
            await this.loadCategoriesDropdown();
        }).finally(async () => {
            await this.loadTagDropdown();
        });

        this.selectedTags = this.cell?.value?.tagIds ? this.getTagsFromTagIds(this.cell?.value?.tagIds) : [];
    },

    methods: {
        cleanErrors() {
            this.validationErrorMessages = null;
            this.apiErrorMessages = null;
            this.newTagError = null;
            this.newTagNameError = null;
        },

        updateSearch(userInput) {
            this.tagInput = userInput;
        },

        async openAddNewTag(newTag) {
            this.cleanErrors();

            await this.loadCategorizedTagsDropdown().finally(async () => {
                await this.loadCategoriesDropdown();
            }).finally(async () => {
                await this.loadTagDropdown();
            });

            this.categories.splice(0, 0, { label: this.$t('funnel.apply.remove.tag.createNewCategory'), id: -1 });
            this.categories.splice(0, 0, { label: this.$t('funnel.apply.remove.tag.noCategory'), id: 0 });
            this.selectedCategoryNew = { id: 0, label: this.$t('funnel.apply.remove.tag.noCategory') };
            this.newTagName = newTag;
            this.newCategoryName = '';
            this.newCategoryLabel = this.$t('funnel.apply.remove.tag.newCategoryLabel');
            this.isNewTagOpen = true;
            this.showSave = false;
        },

        async createNewCategory() {
            let result = null;

            if (this.newCategoryName) {
                result = await createNewCategory({
                    label: this.newCategoryName,
                    description: this.newCategoryName,
                });
                this.selectedCategoryNew.id = result.data;
                await this.loadCategorizedTagsDropdown().finally(async () => {
                    await this.loadCategoriesDropdown();
                });
                this.categories.splice(0, 0, { label: this.$t('funnel.apply.remove.tag.createNewCategory'), id: -1 });
            }

            return result;
        },

        async endNewTag() {
            this.selectedCategoryNew = null;
            this.newTagName = null;
            this.newCategoryName = '';
            this.newCategoryLabel = '';
            this.isNewTagOpen = false;
            this.showSave = true;
        },

        async addNewTag() {
            this.newTagError = null;
            this.newTagNameError = null;
            let result = null;
            let category = this.selectedCategoryNew?.id === -1
                ? { label: this.newCategoryName }
                : this.categories?.find((o) => o.label === this.selectedCategoryNew?.label);
            const tagName = this.newTagName?.length > 0 ? this.newTagName : null;
            const existingElement = this.tags?.find((o) => o.label === tagName && o.category === category?.id);

            if (!existingElement?.label && this.newTagName && ((this.selectedCategoryNew?.id === -1 && this.newCategoryName) || this.selectedCategoryNew?.id !== -1)) {
                this.newTagLabel = this.$t('funnel.apply.remove.tag.tagName');
                this.newCategoryLabel = this.$t('funnel.apply.remove.tag.categoryName');

                if (this.selectedCategoryNew?.id === -1) {
                    result = await this.createNewCategory();
                }

                result = await createNewTag({
                    label: this.newTagName,
                    description: this.newTagName,
                    category: this.selectedCategoryNew?.id,
                });
                await this.loadCategorizedTagsDropdown().finally(async () => {
                    await this.loadCategoriesDropdown();
                }).finally(async () => {
                    await this.loadTagDropdown();
                });

                if (result?.data) {
                    if (this.selectedCategoryNew?.id) {
                        category = this.categories?.find((o) => o.id === this.selectedCategoryNew?.id);

                        this.selectedTags.push({
                            label: this.newTagName,
                            value: `${result.data}L`,
                            category: category?.id,
                            icon: 'tag',
                            categoryName: `${category?.label} >`,
                        });
                    } else {
                        this.selectedTags.push({
                            label: this.newTagName,
                            value: `${result.data}L`,
                            category: null,
                            icon: 'tag',
                            categoryName: '',
                        });
                    }
                    await this.endNewTag();
                }
            } else {
                if (!this.newTagName) {
                    this.newTagNameError = `${this.$t('funnel.apply.remove.tag.tagName')} : ${this.$t('funnel.apply.remove.tag.errRequired')}`;
                }

                if (!((this.selectedCategoryNew?.id === -1 && this.newCategoryName) || this.selectedCategoryNew?.id !== -1)) {
                    this.newTagError = `${this.$t('funnel.apply.remove.tag.categoryName')} : ${this.$t('funnel.apply.remove.tag.errRequired')}`;
                }

                if (existingElement?.label) {
                    this.apiErrorMessages = `${this.$t('funnel.apply.remove.tag.tagName')} : ${this.$t('funnel.apply.remove.tag.errDuplicated')}`;
                }
            }
        },

        async loadTagDropdown() {
            try {
                this.tags = [];
                const { tagDTOList } = await loadTags();

                tagDTOList.forEach((tagDTO) => {
                    if (tagDTO?.name && tagDTO?.id) {
                        this.tags.push({
                            label: tagDTO.name,
                            value: tagDTO.id,
                            category: tagDTO.category?.id,
                            icon: 'tag',
                            categoryName: tagDTO.category?.name ? `${tagDTO.category?.name} >` : '',
                        });
                    }
                });
            } catch (error) {
                this.$error({ message: 'Unable to load tags' });
            }
        },

        async loadCategoriesDropdown() {
            try {
                this.categories = await loadCategories();
            } catch (error) {
                this.$error({ message: 'Unable to load tags' });
            }
        },

        async loadCategorizedTagsDropdown() {
            try {
                this.categorizedTags = await loadCategorizedTags();
            } catch (error) {
                this.$error({ message: 'Unable to load tags' });
            }
        },

        closeModal() {
            this.$emit('close-modal');
        },

        updateConfig() {
            this.validationErrorMessages = null;

            if (this.isReady()) {
                const cell = { ...this.cell };

                cell.value = cell.value || {};
                cell.value.tagIds = this.getIdsFromTags(this.selectedTags);
                cell.value.isApply = this.selectedOption;

                this.$emit('update-cell-value', { cell });
            }
        },

        isReady() {
            let valid = true;

            if (!this.selectedTags || this.selectedTags.length <= 0) {
                valid = false;
                this.validationErrorMessages = this.$t('funnel.apply.remove.tag.errRequired');
            }

            return valid;
        },

        getTagsFromTagIds(tagsParam) {
            const optionsFound = [];

            if (this.tags.length > 0 && tagsParam.length > 0) {
                Object.keys(tagsParam).forEach((tagsIndex) => {
                    const filterResults = this.tags.filter((option) => option.value === tagsParam[tagsIndex]);

                    if (filterResults) {
                        optionsFound.push(filterResults);
                    }
                });
            }

            return optionsFound.flatMap((element) => element).flat(2);
        },

        getIdsFromTags(tagsParam) {
            const tagIds = [];

            Object.keys(tagsParam).forEach((keyOpt) => {
                tagIds.push(tagsParam[keyOpt].value);
            });

            return tagIds;
        },
    },
};
</script>

<style lang="scss" scoped>
.apply-tag-radio-group {
    display: inline-flex;
    margin-right: 0.33rem;
}
</style>
